import type { Level, LogEvent } from "pino";
import { errWithCause } from "pino-std-serializers";
import { buffer, fromEvent, interval, map, Subject, withLatestFrom } from "rxjs";
import { mergeMap, mergeWith, skipUntil } from "rxjs/operators";
import { configure } from "safe-stable-stringify";
import sendLogs from "./sendLogs";
import type { LogMessage } from "./worker";

const LOG_BATCH_MAX_SIZE = 10;
const LOG_BATCH_INTERVAL = 5000;

export interface LogToServerProps {
    accessToken: string;
    collectorUrl: string;
    useWorker?: boolean;
}

let worker: Worker | undefined;

export function enableWorkerLogging() {
    worker ??= new Worker(new URL("./worker.js", import.meta.url), { type: "module" });
}

const logToServerProps$ = new Subject<LogToServerProps>();

const flush$ = interval(LOG_BATCH_INTERVAL).pipe(
    mergeWith(fromEvent(window, "beforeunload")),
    mergeWith(fromEvent(window, "pagehide")),
    mergeWith(fromEvent(document, "visibilitychange")),
    // Don't emit until authorized so we can send events to the backend
    skipUntil(logToServerProps$),
);

const logs$ = new Subject<LogEvent>();
logs$
    .pipe(
        // Aggregate all the log events in time interval or until window event
        buffer(flush$),
        // Split log events batches if the size is too big
        mergeMap((array) => {
            const chunks: LogEvent[][] = [];
            for (let i = 0; i < array.length; i += LOG_BATCH_MAX_SIZE) {
                const chunk = array.slice(i, i + LOG_BATCH_MAX_SIZE);
                chunks.push(chunk);
            }
            return chunks;
        }),
        // Receive access token to be able to send
        withLatestFrom(logToServerProps$),
        // Send log batches
        map(async ([logRecords, { accessToken, collectorUrl }]) => {
            if (!logRecords.length) return;
            const body = safeStringify(logRecords, jsonReplacer);
            if (!body) return;
            if (worker) {
                const message: LogMessage = { body, accessToken, collectorUrl };
                worker.postMessage(message);
                return;
            }
            await sendLogs(body, accessToken, collectorUrl);
        }),
    )
    // We need this to make Observable to work
    .subscribe({
        error: (error) => {
            console.error("==== ERROR in Log Event sending Observable ====", error);
        },
    });

const safeStringify = configure({
    deterministic: false,
    maximumBreadth: 30,
    maximumDepth: 10,
    strict: false,
});

function jsonReplacer(key: string, value: unknown) {
    // Twilio data has a LOT of private members that may hold sensitive information like our auth token
    // These are all prefixed with an underscore, so we can filter them out here
    if (key.startsWith("_")) return undefined;

    // https://github.com/pinojs/pino/issues/2132
    if (value instanceof Error) return errWithCause(value);

    return value;
}

export function initLoggingTransmission(props: { accessToken: string; collectorUrl: string }) {
    logToServerProps$.next(props);
}

export function send(_level: Level, logEvent: LogEvent) {
    logs$.next(logEvent);
}
